import { useEffect } from 'react'
import { Alert, Center, Text } from '@mantine/core'
import * as Sentry from '@sentry/react'

import { ClosingStep } from '@/components/Event/NewScript/components/Closing/ClosingStep'
import { PreparationStep } from '@/components/Event/NewScript/components/Preperation/PreparationStep'
import { AttendanceStep } from '@/components/Event/NewScript/components/ScriptFlow/AttendanceStep'
import { OpeningStep } from '@/components/Event/NewScript/components/ScriptFlow/OpeningStep'
import { QuestioningStep } from '@/components/Event/NewScript/components/ScriptFlow/QuestioningStep'
import { ReviewStep } from '@/components/Event/NewScript/components/ScriptFlow/ReviewStep'
import { useScriptFlowState } from '@/components/Event/NewScript/hooks/state/useScriptFlowState'
import { useParticipantsMapping } from '@/components/Event/NewScript/hooks/useParticipantsMapping'
import { formatStepKey } from '@/components/Event/NewScript/utils/stepKey'
import { ScriptStep } from '@/constants'
import { ScriptStepProps } from '@/types'

const stepMap: { [K in ScriptStep]: React.FC<ScriptStepProps> } = {
  [ScriptStep.PREPARATION]: PreparationStep,
  [ScriptStep.ATTENDANCE]: AttendanceStep,
  [ScriptStep.OPENING]: OpeningStep,
  [ScriptStep.QUESTIONING]: QuestioningStep,
  [ScriptStep.CLOSING]: ClosingStep,
  [ScriptStep.REVIEW]: ReviewStep,
}

const eventScopedSteps = new Set<ScriptStep>([
  ScriptStep.ATTENDANCE,
  ScriptStep.OPENING,
  ScriptStep.QUESTIONING,
  ScriptStep.CLOSING,
])

export const ScriptFlow = ({ event, refetchEvent }: ScriptStepProps) => {
  const { state, currentStep, currentEventIndex, currentEvent } =
    useScriptFlowState()
  const isMappingEnabled =
    currentEventIndex !== undefined &&
    state.steps[formatStepKey(ScriptStep.ATTENDANCE, currentEventIndex)]
      ?.actions['attendees']?.completed === true &&
    // if the event is canceled/CNA/Rescheduled we can disable the mapping
    state.events[currentEventIndex]?.first_skipped_step === null

  useParticipantsMapping({
    event: event,
    enabled: isMappingEnabled,
  })

  const StepComponent = stepMap[currentStep]
  const isMissingEventScopedState =
    eventScopedSteps.has(currentStep) && !currentEvent

  useEffect(() => {
    if (!StepComponent || isMissingEventScopedState) {
      Sentry.captureException(new Error('Invalid script flow state'), {
        extra: {
          eventId: event.id,
          currentStep: state.current_step,
          parsedStep: currentStep,
          currentEventIndex,
          eventCount: state.events.length,
          hasStepComponent: Boolean(StepComponent),
        },
      })
    }
  }, [
    StepComponent,
    currentEventIndex,
    currentStep,
    event.id,
    isMissingEventScopedState,
    state.current_step,
    state.events.length,
  ])

  if (!StepComponent || isMissingEventScopedState) {
    return <ScriptStateRecoveryFallback />
  }

  return <StepComponent event={event} refetchEvent={refetchEvent} />
}

const ScriptStateRecoveryFallback = () => (
  <Center>
    <Alert color="red">
      <Text align="center" size="lg" fw={600}>
        Sorry, we couldn't load this script step.
      </Text>
      <Text size="md">
        Please refresh the page. If this keeps happening, contact support so we
        can recover the script state for this event.
      </Text>
    </Alert>
  </Center>
)
